import { asyncCallback } from "../utils/analysis-async";
import { StepProcess } from "../interfaces/stepProcess";
import {
  COMP_AUTH_MAPPING,
  DKIM_MAPPING,
  DMARC_MAPPING,
  DMARC_POLICY,
  DOMAIN_CONFIG_KEYS,
  HEADERS,
  HEADERS_KEYS,
  IMPERSONATION_RISK_VALUES,
  SPF_MAPPING
} from "../models/constants";
import { DomainDTO, NodeData, ResultInfo } from "../models/shared.model";
import { StepResult } from "../models/step-result";
import { StringUtils } from "../utils/string.utils";
import { MailUtils } from "../utils/mail.utils";

export class IsInternal extends StepProcess {
  process = (data?: NodeData, info?: ResultInfo): Promise<StepResult> => {
    return asyncCallback<any>(async (resolve, reject) => {

      if (await this.domainService.checkInternalEmail())
        resolve(new StepResult(true, this.translate.instant('risk_flow.internal_email'),
          { ...info, isInternal: true, dkim: undefined, spf: undefined, dmarc: undefined }));

      let reasonLog = this.translate.instant('risk_flow.not_internal_email', {
        sender: data.domain,
        receiver: MailUtils.parseMultiLevelDomain(Office.context.mailbox.userProfile.emailAddress)
      });


      resolve(new StepResult(false, reasonLog, {...info, isInternal: false}));
    });
  };
}

export class CheckCompAuth extends StepProcess {
  process = (data?: NodeData, info?: ResultInfo): Promise<StepResult> => {
    let compoAuthRes: string;

    return asyncCallback<any>(async (resolve, reject) => {
      try {
        if (this.run.item != null) {

          let reasonLog = "";
          const authHeader = this.messageService.getMessageHeaderValue(HEADERS.AUTHENTICATION_RESULTS);

          const compAuthValue = authHeader ? StringUtils.extractKeyValue(authHeader, HEADERS_KEYS.COMP_AUTH) : null;

          info = {...info, compAuth: compAuthValue};

          const compAuthMapping = COMP_AUTH_MAPPING;

          if (compAuthValue && compAuthMapping[compAuthValue]) {
            compoAuthRes = compAuthMapping[compAuthValue].result;
            reasonLog = this.translate.instant(compAuthMapping[compAuthValue].reasonKey);
          } else {
            compoAuthRes = IMPERSONATION_RISK_VALUES.PASS.toString();
          }

          resolve(new StepResult(compoAuthRes, reasonLog, info));
        }
      } catch (e) {
        console.log(this.translate.instant('risk_flow.dkim_enabled_error', { error: e }));
        reject(e);
      }
    });
  };

}

export class IsDKIMEnabled extends StepProcess {
  process = (data?: NodeData, info?: ResultInfo): Promise<StepResult> => {
    let dkimResult: string | undefined;

    return asyncCallback<any>(async (resolve, reject) => {
      try {
        if (this.run.item != null) {

          let reasonLog = "";
          const authHeader = this.messageService.getMessageHeaderValue(HEADERS.AUTHENTICATION_RESULTS);
          const isInternal = info.isInternal === true;
          const dkimValue = authHeader ? StringUtils.extractKeyValue(authHeader, HEADERS_KEYS.DKIM) : null;
          const isDomainDkimSetup = this.domainService.checkDomainConfig(data.domain, DOMAIN_CONFIG_KEYS.DKIM);

          info = {...info, domainDkimSetup: isDomainDkimSetup};
          const shouldUpdateDkim = this.shouldUpdateDkim(info.domain);

          if (shouldUpdateDkim && dkimValue === IMPERSONATION_RISK_VALUES.PASS) {
            try {
              this.domainService.updateDkimRecords(info.domain);
            } catch (error) {
              console.error(`Failed to update DKIM for ${info.domain}`, error);
            }
          }

          if (!authHeader) {
            if (await this.domainService.checkInternalEmail()) {
              dkimResult = undefined;
              reasonLog = this.translate.instant('risk_flow.dkim_is_internal');
            } else {
              dkimResult = IMPERSONATION_RISK_VALUES.FAIL.toString();
              reasonLog = this.translate.instant('risk_flow.missing_authentication_results', { header: HEADERS.AUTHENTICATION_RESULTS });
            }
          } else {
            const dkimMapping = DKIM_MAPPING
            if (dkimValue && dkimMapping[dkimValue]) {
              dkimResult = dkimMapping[dkimValue].result;
              reasonLog = this.translate.instant(dkimMapping[dkimValue].reasonKey, {
                header: HEADERS.AUTHENTICATION_RESULTS,
                value: dkimValue,
              });
            } else {
              dkimResult = IMPERSONATION_RISK_VALUES.FAIL.toString();
            }

            if (isInternal && (!dkimValue || dkimValue === IMPERSONATION_RISK_VALUES.NONE || dkimValue === IMPERSONATION_RISK_VALUES.PASS)) {
              reasonLog = this.translate.instant(dkimValue ? 'risk_flow.dkim_internal' : 'risk_flow.dkim_missing_internal', {
                header: HEADERS.AUTHENTICATION_RESULTS,
                value: dkimValue
              });
              return resolve(new StepResult(IMPERSONATION_RISK_VALUES.PASS.toString(), reasonLog, { ...info, dkim: undefined }));
            }

          }

          resolve(new StepResult(dkimResult, reasonLog, { ...info,dkim: dkimResult }));
        }
      } catch (e) {
        console.log(this.translate.instant('risk_flow.dkim_enabled_error', { error: e }));
        reject(e);
      }
    });
  };

  private shouldUpdateDkim(domain: string): boolean {
    const trustedDomains: DomainDTO[] = this.domainService.getDomains();
    const matchedDomain = trustedDomains.find(item =>
      StringUtils.compareCaseInsensitive(item.domain, domain)
    );

    if (matchedDomain) {
      return !matchedDomain.dkim;
    }

    const personalTrusted = this.domainService.loadPersonalTrustedListFromCache();
    const personalDomain = personalTrusted.domains?.get(domain.toLowerCase())
    return !!personalDomain && !personalDomain.dkim;
  }

}

export class IsSPFConfigured extends StepProcess {
  process = (data?: NodeData, info?: ResultInfo): Promise<StepResult> => {
    let spfResult: string | undefined;
    return asyncCallback<any>(async (resolve, reject) => {

      try {
        if (this.run.item != null) {

          let reasonLog = "";
          const authHeader = this.messageService.getMessageHeaderValue(HEADERS.AUTHENTICATION_RESULTS);
          const isInternal = info.isInternal === true;
          const spfValue = authHeader ? StringUtils.extractKeyValue(authHeader, HEADERS_KEYS.SPF) : null;
          const isDomainSpfSetup = this.domainService.checkDomainConfig(data.domain, DOMAIN_CONFIG_KEYS.SPF);

          info = {...info, domainSpfSetup: isDomainSpfSetup};

          if (!authHeader) {
            if (await this.domainService.checkInternalEmail()) {
              spfResult = undefined;
              reasonLog = this.translate.instant("risk_flow.spf_is_internal");
            } else {
              spfResult = IMPERSONATION_RISK_VALUES.FAIL.toString();
              reasonLog = this.translate.instant("risk_flow.header_missing", { header: HEADERS.AUTHENTICATION_RESULTS });
            }
          } else {
            const spfMapping = SPF_MAPPING;
            if (spfValue && spfMapping[spfValue]) {
              spfResult = spfMapping[spfValue].result;
              reasonLog = this.translate.instant(spfMapping[spfValue].reasonKey, {
                header: HEADERS.AUTHENTICATION_RESULTS,
                value: spfValue,
              });
            } else {
              spfResult = IMPERSONATION_RISK_VALUES.FAIL.toString();
            }

            if (
                isInternal &&
                (!spfValue ||
                    spfValue === IMPERSONATION_RISK_VALUES.NONE ||
                    spfValue === IMPERSONATION_RISK_VALUES.PASS)
            ) {
              reasonLog = this.translate.instant(
                  spfValue ? "risk_flow.spf_internal" : "risk_flow.spf_missing_internal",
                  { header: HEADERS.AUTHENTICATION_RESULTS, value: spfValue }
              );
              return resolve(new StepResult(IMPERSONATION_RISK_VALUES.PASS.toString(), reasonLog, { ...info, spf: undefined }));
            }
          }

          resolve(new StepResult(spfResult, reasonLog, { ...info, spf: spfResult }));
        }
      } catch (e) {
        console.log(this.translate.instant('risk_flow.spf_enabled', { error: e }));
        reject(e);
      }
    });
  };
}

export class IsItForwardedEmail extends StepProcess {
  process = (data?: NodeData, info?: ResultInfo): Promise<StepResult> => {

    return Promise.resolve().then(() => {
      this.run.assertActive();
      if (!this.run.headersAvailable) throw new Error('Message headers unavailable');
      const check = this.checkIfForwarded(String(this.messageService.getHeadersAsString()));
      return new StepResult(check.isForwarded, check.reasonLog);
    });
  };

  private checkIfForwarded(headers: string): { isForwarded: boolean, reasonLog: string } {
    // Convert headers to lowercase for case-insensitive comparison
    const headersLower = headers.toLowerCase();
    // Check for common forwarded headers
    const isForwarded =
      headersLower.includes(HEADERS_KEYS.X_FORWARDED_FOR) ||
      headersLower.includes(HEADERS_KEYS.FORWARDED) ||
      headersLower.includes(HEADERS_KEYS.RECEIVED) && headersLower.includes(HEADERS_KEYS.WITH_ESMTP_ID);

    let reasonLog = headersLower.includes(HEADERS_KEYS.X_FORWARDED_FOR)
        ? this.translate.instant("risk_flow.forwarded_x_forwarded_for", { key: HEADERS_KEYS.X_FORWARDED_FOR })
        : (headersLower.includes(HEADERS_KEYS.FORWARDED)
            ? this.translate.instant("risk_flow.forwarded", { key: HEADERS_KEYS.FORWARDED })
            : (headersLower.includes(HEADERS_KEYS.RECEIVED) && headersLower.includes(HEADERS_KEYS.WITH_ESMTP_ID)
                ? this.translate.instant("risk_flow.received_esmpt", { key1: HEADERS_KEYS.RECEIVED, key2: HEADERS_KEYS.WITH_ESMTP_ID })
                : this.translate.instant("risk_flow.not_forwarded")));


    return { isForwarded, reasonLog };
  }
}

export class CheckDMARCConfiguration extends StepProcess {
  process = (data?: NodeData, info?: ResultInfo): Promise<StepResult> => {
    let dmarcResult : string | undefined;

    return asyncCallback<any>(async (resolve, reject) => {
      try {
        if (this.run.item != null) {
          let reasonLog = "";
          const authHeader = this.messageService.getMessageHeaderValue(HEADERS.AUTHENTICATION_RESULTS);
          const isInternal = info.isInternal === true;
          const dmarcValue = authHeader ? StringUtils.extractKeyValue(authHeader, HEADERS_KEYS.DMARC) : null;
          const isDomainDmarcSetup = this.domainService.checkDomainConfig(data.domain, DOMAIN_CONFIG_KEYS.DMARC);

          info = {...info, domainDmarcSetup: isDomainDmarcSetup};

          if (!authHeader) {
            if (await this.domainService.checkInternalEmail()) {
              dmarcResult = undefined;
              reasonLog = this.translate.instant("risk_flow.dmarc_internal");
            } else {
              dmarcResult = IMPERSONATION_RISK_VALUES.FAIL.toString();
              reasonLog = this.translate.instant("risk_flow.dmarc_header_missing", { header: HEADERS.AUTHENTICATION_RESULTS });
            }
          } else {
            const dmarcMapping = DMARC_MAPPING

            if (dmarcValue && dmarcMapping[dmarcValue]) {
              dmarcResult = dmarcMapping[dmarcValue].result;
              reasonLog = this.translate.instant(dmarcMapping[dmarcValue].reasonKey, {
                header: HEADERS.AUTHENTICATION_RESULTS,
                value: dmarcValue,
              });
            } else {
              dmarcResult = IMPERSONATION_RISK_VALUES.FAIL.toString();
            }

            if (isInternal && (!dmarcValue || dmarcValue === IMPERSONATION_RISK_VALUES.NONE || dmarcValue === IMPERSONATION_RISK_VALUES.PASS)) {
              reasonLog = this.translate.instant(dmarcValue ? "risk_flow.check_dmarc_internal" : "risk_flow.check_dmarc_missing_internal", {
                header: HEADERS.AUTHENTICATION_RESULTS,
                value: dmarcValue
              });
              return resolve(new StepResult(IMPERSONATION_RISK_VALUES.PASS.toString(), reasonLog, { ...info, dmarc: undefined }));
            }
          }

          resolve(new StepResult(dmarcResult, reasonLog, { ...info, dmarc: dmarcResult }));
        }
      } catch (e) {
        console.log(this.translate.instant("risk_flow.check_dmarc_error", { error: e }));
        reject(e);
      }
    });
  };
}

export class CheckDMARCPolicy extends StepProcess {
  process = (data?: NodeData, info?: ResultInfo): Promise<StepResult> => {
    let dmarcPolicy: string = DMARC_POLICY.NONE;

    return asyncCallback<any>(async (resolve, reject) => {
      try {
        if (this.run.item != null) {

          let reasonLog = "";
          const authHeader: string = this.messageService.getMessageHeaderValue(HEADERS.AUTHENTICATION_RESULTS);

          if (authHeader.includes(HEADERS_KEYS.ACTION)) {
            dmarcPolicy = authHeader.includes(HEADERS_KEYS.ACTION_NONE) ? DMARC_POLICY.NONE : (authHeader.includes(HEADERS_KEYS.ACTION_REJECT) ? DMARC_POLICY.REJECT : DMARC_POLICY.QUARANTINE);
            let headerValue = authHeader.includes(HEADERS_KEYS.ACTION_NONE) ? HEADERS_KEYS.ACTION_NONE : (authHeader.includes(HEADERS_KEYS.ACTION_REJECT) ? HEADERS_KEYS.ACTION_REJECT : null);
            reasonLog = headerValue
                ? this.translate.instant("risk_flow.header_has_value", {
                  header: HEADERS.AUTHENTICATION_RESULTS,
                  value: headerValue
                })
                : this.translate.instant("risk_flow.header_missing_action", {
                  header: HEADERS.AUTHENTICATION_RESULTS,
                  actionKey: HEADERS_KEYS.ACTION,
                  actionNone: HEADERS_KEYS.ACTION_NONE,
                  actionReject: HEADERS_KEYS.ACTION_REJECT
                });
          } else if (authHeader.includes(HEADERS_KEYS.P)) {
            dmarcPolicy = authHeader.includes(HEADERS_KEYS.P_NONE) ? DMARC_POLICY.NONE : (authHeader.includes(HEADERS_KEYS.P_REJECT) ? DMARC_POLICY.REJECT : DMARC_POLICY.QUARANTINE);
            let headerValue = authHeader.includes(HEADERS_KEYS.P_NONE) ? HEADERS_KEYS.P_NONE : (authHeader.includes(HEADERS_KEYS.P_REJECT) ? HEADERS_KEYS.P_REJECT : null);
            reasonLog = headerValue
                ? this.translate.instant("risk_flow.header_has_value", {
                  header: HEADERS.AUTHENTICATION_RESULTS,
                  value: headerValue
                })
                : this.translate.instant("risk_flow.header_missing_p", {
                  header: HEADERS.AUTHENTICATION_RESULTS,
                  pKey: HEADERS_KEYS.P,
                  pNone: HEADERS_KEYS.P_NONE,
                  pReject: HEADERS_KEYS.P_REJECT
                });

          } else {
            reasonLog = this.translate.instant("risk_flow.header_missing_both", {
              header: HEADERS.AUTHENTICATION_RESULTS,
              actionKey: HEADERS_KEYS.ACTION,
              pKey: HEADERS_KEYS.P
            });

          }

          resolve(new StepResult(dmarcPolicy, reasonLog, { ...info, dmarcPolicy: dmarcPolicy }));
        }
      } catch (e) {
        console.log(this.translate.instant("risk_flow.check_dmarc_configuration", { error: e }));
        reject(e);
      }
    });
  };
}
